import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatRupiah, formatApiError, todayStr } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const QuotationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const isEdit = !!id;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_id: "",
    date: todayStr(),
    valid_until: "",
    items: [{ product_id: null, name: "", qty: 1, price: 0 }],
    discount_amount: 0,
    tax_percent: 0,
    notes: "",
    status: "draft",
  });

  useEffect(() => {
    api.get("/customers", { params: { limit: 1000 } }).then((r) => setCustomers(r.data.items));
    api.get("/products", { params: { limit: 1000, status: "active" } }).then((r) => setProducts(r.data.items));
  }, []);

  useEffect(() => {
    if (settings && !isEdit) setForm((f) => ({ ...f, tax_percent: settings.tax_default ?? 0 }));
  }, [settings, isEdit]);

  useEffect(() => {
    if (isEdit) {
      api.get(`/quotations/${id}`).then((r) => {
        const d = r.data;
        setForm({
          customer_id: d.customer_id,
          date: d.date,
          valid_until: d.valid_until || "",
          items: d.items?.length ? d.items : [{ product_id: null, name: "", qty: 1, price: 0 }],
          discount_amount: d.discount_amount || 0,
          tax_percent: d.tax_percent || 0,
          notes: d.notes || "",
          status: d.status || "draft",
        });
      });
    }
  }, [id, isEdit]);

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, i) => s + Number(i.qty || 0) * Number(i.price || 0), 0);
    const afterDisc = Math.max(subtotal - Number(form.discount_amount || 0), 0);
    const tax = (afterDisc * Number(form.tax_percent || 0)) / 100;
    return { subtotal, tax, grand: afterDisc + tax };
  }, [form]);

  const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: null, name: "", qty: 1, price: 0 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const pickProduct = (idx, pid) => { const p = products.find((x) => x.id === pid); if (p) setItem(idx, { product_id: p.id, name: p.name, price: p.sell_price }); };

  const save = async () => {
    if (!form.customer_id) { toast.error("Pilih customer"); return; }
    if (form.items.every((i) => !i.name)) { toast.error("Tambah minimal 1 item"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        valid_until: form.valid_until || null,
        discount_amount: Number(form.discount_amount),
        tax_percent: Number(form.tax_percent),
        items: form.items.filter((i) => i.name).map((i) => ({ ...i, qty: Number(i.qty), price: Number(i.price) })),
      };
      const res = isEdit ? await api.put(`/quotations/${id}`, payload) : await api.post("/quotations", payload);
      toast.success(isEdit ? "Penawaran diperbarui" : "Penawaran dibuat");
      navigate(`/quotations/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate("/quotations")} className="-ml-2"><ArrowLeft className="mr-2 h-4 w-4" />Kembali</Button>
      <PageHeader title={isEdit ? "Edit Penawaran" : "Buat Penawaran"} subtitle="Lengkapi detail penawaran harga" />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="quotation-customer-select"><SelectValue placeholder="Pilih customer" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Disetujui</SelectItem>
                    <SelectItem value="rejected">Ditolak</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tanggal</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Berlaku Hingga</Label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading font-semibold">Item</h3>
              <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn"><Plus className="mr-1 h-4 w-4" />Item</Button>
            </div>
            <div className="space-y-3">
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 rounded-lg border border-border p-3">
                  <div className="col-span-12 space-y-1 sm:col-span-5">
                    <Label className="text-xs">Produk / Deskripsi</Label>
                    <Select value={it.product_id || ""} onValueChange={(v) => pickProduct(idx, v)}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Pilih produk (opsional)" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="mt-1 h-9" placeholder="atau ketik manual" value={it.name} onChange={(e) => setItem(idx, { name: e.target.value, product_id: null })} data-testid={`item-name-${idx}`} />
                  </div>
                  <div className="col-span-3 space-y-1 sm:col-span-2">
                    <Label className="text-xs">Qty</Label>
                    <Input type="number" className="h-9" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} data-testid={`item-qty-${idx}`} />
                  </div>
                  <div className="col-span-6 space-y-1 sm:col-span-3">
                    <Label className="text-xs">Harga</Label>
                    <Input type="number" className="h-9" value={it.price} onChange={(e) => setItem(idx, { price: e.target.value })} data-testid={`item-price-${idx}`} />
                  </div>
                  <div className="col-span-3 flex items-end justify-end sm:col-span-2">
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeItem(idx)} disabled={form.items.length === 1}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 space-y-2">
            <Label>Catatan</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-4 rounded-xl border border-border bg-card p-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
            <h3 className="font-heading font-semibold">Ringkasan</h3>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-medium">{formatRupiah(totals.subtotal)}</span></div>
            <div className="space-y-2"><Label className="text-xs">Diskon (Rp)</Label><Input type="number" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} /></div>
            <div className="space-y-2"><Label className="text-xs">PPN / Pajak (%)</Label><Input type="number" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} /></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Pajak</span><span className="font-medium">{formatRupiah(totals.tax)}</span></div>
            <div className="border-t border-border pt-3 flex justify-between">
              <span className="font-heading font-semibold">Grand Total</span>
              <span className="font-heading text-lg font-bold text-primary">{formatRupiah(totals.grand)}</span>
            </div>
            <Button className="w-full" size="lg" onClick={save} disabled={saving} data-testid="save-quotation-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{isEdit ? "Perbarui" : "Simpan"} Penawaran
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationForm;
